import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { getCoach, getCoachMemberships } from '../utils/coaches';
import { getUserProfile } from '../utils/database';
import { getAgeGroup } from '../utils/ageGroups';
import DashboardSidebar from '../components/DashboardSidebar';
import BottomNav from '../components/BottomNav';
import '../styles/Dashboard.css';

function CoachDashboard() {
  const { userId, coachId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [coach, setCoach] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser || currentUser.uid !== userId) {
        navigate('/signin');
        return;
      }
      const userData = await getUserProfile(userId);
      setUser(userData);
    });
    return () => unsubscribe();
  }, [userId, navigate]);

  useEffect(() => {
    if (!coachId || !user) return;
    const load = async () => {
      try {
        const coachData = await getCoach(coachId);
        if (!coachData || coachData.ownerId !== userId) {
          navigate(`/${userId}/dashboard`);
          return;
        }
        setCoach(coachData);

        const mems = await getCoachMemberships(coachId);
        const profiles = await Promise.all(mems.map((m) => getUserProfile(m.userId)));
        setPlayers(
          mems.map((m, i) => ({
            ...m,
            profile: profiles[i],
            ageGroup: getAgeGroup(profiles[i]?.birthdate),
          }))
        );
      } catch (err) {
        console.error('[CoachDashboard] Error:', err);
        navigate(`/${userId}/dashboard`);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [coachId, userId, user, navigate]);

  if (loading || !coach) {
    return (
      <div className="dashboard-page">
        <div className="dashboard-main">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const landingUrl = `${window.location.origin}/coach/${coach.slug}`;

  return (
    <div className="dashboard-page has-bottom-nav">
      <div className="dashboard-bg-glow dashboard-bg-glow-1" />
      <div className="dashboard-bg-glow dashboard-bg-glow-2" />
      <div className="dashboard-overlay" />
      <DashboardSidebar userId={userId} user={user} />
      <div className="dashboard-main">
        <div className="dashboard-header">
          <button
            type="button"
            onClick={() => navigate(`/${userId}/settings`)}
            style={{ background: 'none', border: 'none', color: 'inherit', fontSize: '14px', cursor: 'pointer', marginBottom: '8px' }}
          >
            &larr; Settings
          </button>
          <h1 className="dashboard-greeting">{coach.name}</h1>
          {coach.ageGroup && (
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', margin: '4px 0 0 0' }}>{coach.ageGroup}</p>
          )}
        </div>

        <div className="settings-content">
          {/* Landing page link */}
          <div className="dashboard-card" style={{ marginBottom: '24px' }}>
            <h3 className="settings-section-header" style={{ marginBottom: '12px' }}>Your Team Page</h3>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="text"
                readOnly
                value={landingUrl}
                className="settings-input"
                style={{ flex: 1, minWidth: '200px', fontSize: '14px' }}
              />
              <button
                type="button"
                className="c-ripple"
                onClick={() => navigator.clipboard.writeText(landingUrl)}
                style={{ padding: '8px 16px', background: '#FF0000', color: 'white', border: 'none', fontSize: '14px', borderRadius: '6px' }}
              >
                Copy
              </button>
            </div>
          </div>

          {/* Markup info */}
          <div className="dashboard-card" style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>Your add-on per athlete</span>
              <span style={{ fontWeight: 600 }}>${coach.markupPerMonth}/mo</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
              <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>Stripe</span>
              <span style={{ color: coach.stripeAccountId ? '#00C864' : 'rgba(255,255,255,0.4)', fontSize: '14px' }}>
                {coach.stripeAccountId ? 'Connected' : 'Not connected (coming soon)'}
              </span>
            </div>
          </div>

          {/* Roster */}
          <div className="dashboard-card" style={{ marginBottom: '24px' }}>
            <h3 className="settings-section-header" style={{ marginBottom: '16px' }}>
              Roster ({players.length} {players.length === 1 ? 'athlete' : 'athletes'})
            </h3>
            {players.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {players.map((p) => (
                  <div
                    key={p.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px',
                      background: 'rgba(255,255,255,0.05)',
                      borderRadius: '8px',
                    }}
                  >
                    <span>{p.profile?.firstName} {p.profile?.lastName}</span>
                    <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>
                      {p.profile?.birthdate ? new Date().getFullYear() - new Date(p.profile.birthdate).getFullYear() : '—'} yrs
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', margin: 0 }}>
                  No athletes yet. Share your team page to invite players.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      <BottomNav userId={userId} user={user} />
    </div>
  );
}

export default CoachDashboard;
